import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Edit3, Trash2, GraduationCap, Briefcase, BookOpen, Settings, Search, ChevronLeft, ChevronRight, Award, Globe, Code } from 'lucide-react';
import api from '../components/api';
import { toast } from 'react-toastify';
import useGlobale from '../components/global';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import DemandeurMenu from '../components/DemandeurMenu';

const ParametrePage = () => {
    const { user } = useGlobale();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('niveauEtude'); // Options: niveauEtude, typeFormation, domaineFormation, typeDiplome, listeLangue
    const [loading, setLoading] = useState(true);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);

    // État pour les différents référentiels
    const [niveauxEtude, setNiveauxEtude] = useState([]);
    const [typesFormation, setTypesFormation] = useState([]);
    const [domainesFormation, setDomainesFormation] = useState([]);
    const [typesDiplome, setTypesDiplome] = useState([]);
    const [listeLangues, setListeLangues] = useState([]);
    const [competences, setCompetences] = useState([]);

    // États pour la recherche
    const [searchTerm, setSearchTerm] = useState('');

    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);

    // État pour les formulaires
    const [formData, setFormData] = useState({
        niveau: '',
        libelle: '',
        nom: '',
        langue: ''
    });

    // État pour les erreurs de formulaire
    const [errors, setErrors] = useState({});

    // État pour l'élément sélectionné
    const [selectedItem, setSelectedItem] = useState(null);

    // Set sidebar initial state based on screen size
    useEffect(() => {
        const handleResize = () => {
            setIsSidebarOpen(window.innerWidth >= 1024);
        };

        // Set initial state
        handleResize();

        // Add event listener
        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch data when tab changes
    useEffect(() => {
        fetchData();
        // Réinitialiser la recherche et la pagination lors du changement d'onglet
        setSearchTerm('');
        setCurrentPage(1);
    }, [activeTab]);

    const fetchData = async () => {
        setLoading(true);
        try {
            if (activeTab === 'niveauEtude') {
                const response = await api.get('demandeur/niveaux-etude/');
                setNiveauxEtude(response.data);
            } else if (activeTab === 'typeFormation') {
                const response = await api.get('demandeur/types-formation/');
                setTypesFormation(response.data);
            } else if (activeTab === 'domaineFormation') {
                const response = await api.get('demandeur/domaines-formation/');
                setDomainesFormation(response.data);
            } else if (activeTab === 'typeDiplome') {
                const response = await api.get('demandeur/types-diplome/');
                setTypesDiplome(response.data);
            } else if (activeTab === 'listeLangue') {
                const response = await api.get('demandeur/liste-langues/');
                setListeLangues(response.data);
            } else if (activeTab === 'competence') {
                const response = await api.get('demandeur/competences/');
                setCompetences(response.data);
            }
        } catch (error) {
            console.error(`Erreur lors de la récupération des données:`, error);
            toast.error(`Erreur lors de la récupération des données`);
        } finally {
            setLoading(false);
        }
    };

    // Fonction pour formater la date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).replace(',', ' à');
    };

    // Fonction pour filtrer les données selon le terme de recherche
    const getFilteredData = () => {
        let data = [];
        
        if (activeTab === 'niveauEtude') {
            data = niveauxEtude;
        } else if (activeTab === 'typeFormation') {
            data = typesFormation;
        } else if (activeTab === 'domaineFormation') {
            data = domainesFormation;
        } else if (activeTab === 'typeDiplome') {
            data = typesDiplome;
        } else if (activeTab === 'listeLangue') {
            data = listeLangues;
        } else if (activeTab === 'competence') {
            data = competences;
        }
        
        if (!searchTerm.trim()) return data;
        
        return data.filter(item => {
            if (activeTab === 'niveauEtude') {
                return item.niveau.toLowerCase().includes(searchTerm.toLowerCase());
            } else if (activeTab === 'typeFormation') {
                return item.libelle.toLowerCase().includes(searchTerm.toLowerCase());
            } else if (activeTab === 'domaineFormation') {
                return item.nom.toLowerCase().includes(searchTerm.toLowerCase());
            } else if (activeTab === 'typeDiplome') {
                return item.nom.toLowerCase().includes(searchTerm.toLowerCase());
            } else if (activeTab === 'listeLangue') {
                return item.langue.toLowerCase().includes(searchTerm.toLowerCase());
            } else if (activeTab === 'competence') {
                return item.nom.toLowerCase().includes(searchTerm.toLowerCase());
            }
            return false;
        });
    };

    // Fonction pour obtenir les données paginées
    const getPaginatedData = () => {
        const filteredData = getFilteredData();
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredData.slice(startIndex, startIndex + itemsPerPage);
    };

    // Calculer le nombre total de pages
    const totalPages = Math.ceil(getFilteredData().length / itemsPerPage);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        // Effacer l'erreur lorsqu'un champ est modifié
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Réinitialiser à la première page lors de la recherche
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1); // Réinitialiser à la première page lors du changement d'items par page
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (activeTab === 'niveauEtude' && !formData.niveau.trim()) {
            newErrors.niveau = "Le niveau d'étude est requis";
        }
        
        if (activeTab === 'typeFormation' && !formData.libelle.trim()) {
            newErrors.libelle = "Le libellé du type de formation est requis";
        }
        
        if (activeTab === 'domaineFormation' && !formData.nom.trim()) {
            newErrors.nom = "Le nom du domaine de formation est requis";
        }
        
        if (activeTab === 'typeDiplome' && !formData.nom.trim()) {
            newErrors.nom = "Le nom du type de diplôme est requis";
        }
        
        if (activeTab === 'listeLangue' && !formData.langue.trim()) {
            newErrors.langue = "Le nom de la langue est requis";
        }

        if (activeTab === 'competence' && !formData.nom.trim()) {
            newErrors.nom = "Le nom de la compétence est requis";
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);
        try {
            let response;
            let endpoint = '';
            let payload = {};
            
            if (activeTab === 'niveauEtude') {
                endpoint = isEditMode ? `demandeur/niveaux-etude/${selectedItem.id}/` : 'demandeur/niveaux-etude/';
                payload = {
                    niveau: formData.niveau,
                    utilisateur: user.id
                };
            } else if (activeTab === 'typeFormation') {
                endpoint = isEditMode ? `demandeur/types-formation/${selectedItem.id}/` : 'demandeur/types-formation/';
                payload = {
                    libelle: formData.libelle
                };
            } else if (activeTab === 'domaineFormation') {
                endpoint = isEditMode ? `demandeur/domaines-formation/${selectedItem.id}/` : 'demandeur/domaines-formation/';
                payload = {
                    nom: formData.nom
                };
            } else if (activeTab === 'typeDiplome') {
                endpoint = isEditMode ? `demandeur/types-diplome/${selectedItem.id}/` : 'demandeur/types-diplome/';
                payload = {
                    nom: formData.nom
                };
            } else if (activeTab === 'listeLangue') {
                endpoint = isEditMode ? `demandeur/liste-langues/${selectedItem.id}/` : 'demandeur/liste-langues/';
                payload = {
                    langue: formData.langue
                };
            } else if (activeTab === 'competence') {
                endpoint = isEditMode ? `demandeur/competences/${selectedItem.id}/` : 'demandeur/competences/';
                payload = {
                    nom: formData.nom
                };
            }
            
            if (isEditMode) {
                response = await api.put(endpoint, payload);
                toast.success(`${getEntityName()} mis à jour avec succès`);
            } else {
                response = await api.post(endpoint, payload);
                toast.success(`${getEntityName()} ajouté avec succès`);
            }
            
            // Mise à jour des données
            fetchData();
            setShowModal(false);
            resetForm();
        } catch (error) {
            console.error(`Erreur lors de la sauvegarde:`, error);
            
            if (error.response && error.response.data) {
                if (typeof error.response.data === 'object') {
                    setErrors(error.response.data);
                } else {
                    toast.error(`Erreur lors de la sauvegarde`);
                }
            } else {
                toast.error('Erreur de connexion au serveur');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        setLoading(true);
        try {
            let endpoint = '';
            
            if (activeTab === 'niveauEtude') {
                endpoint = `demandeur/niveaux-etude/${id}/`;
            } else if (activeTab === 'typeFormation') {
                endpoint = `demandeur/types-formation/${id}/`;
            } else if (activeTab === 'domaineFormation') {
                endpoint = `demandeur/domaines-formation/${id}/`;
            } else if (activeTab === 'typeDiplome') {
                endpoint = `demandeur/types-diplome/${id}/`;
            } else if (activeTab === 'listeLangue') {
                endpoint = `demandeur/liste-langues/${id}/`;
            } else if (activeTab === 'competence') {
                endpoint = `demandeur/competences/${id}/`;
            }
            
            await api.delete(endpoint);
            toast.success(`${getEntityName()} supprimé avec succès`);
            setDeleteConfirmation(null);
            
            // Mise à jour des données
            fetchData();
        } catch (error) {
            console.error(`Erreur lors de la suppression:`, error);
            toast.error(`Erreur lors de la suppression`);
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = (item) => {
        setIsEditMode(true);
        setSelectedItem(item);
        
        if (activeTab === 'niveauEtude') {
            setFormData({
                niveau: item.niveau,
                libelle: '',
                nom: '',
                langue: ''
            });
        } else if (activeTab === 'typeFormation') {
            setFormData({
                niveau: '',
                libelle: item.libelle,
                nom: '',
                langue: ''
            });
        } else if (activeTab === 'domaineFormation') {
            setFormData({
                niveau: '',
                libelle: '',
                nom: item.nom,
                langue: ''
            });
        } else if (activeTab === 'typeDiplome') {
            setFormData({
                niveau: '',
                libelle: '',
                nom: item.nom,
                langue: ''
            });
        } else if (activeTab === 'listeLangue') {
            setFormData({
                niveau: '',
                libelle: '',
                nom: '',
                langue: item.langue
            });
        } else if (activeTab === 'competence') {
            setFormData({
                niveau: '',
                libelle: '',
                nom: item.nom,
                langue: ''
            });
        }
        
        setShowModal(true);
    };

    const openAddModal = () => {
        setIsEditMode(false);
        setSelectedItem(null);
        resetForm();
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setErrors({});
    };

    const resetForm = () => {
        setFormData({
            niveau: '',
            libelle: '',
            nom: '',
            langue: ''
        });
    };

    // Fonction pour obtenir le nom de l'entité courante
    const getEntityName = () => {
        if (activeTab === 'niveauEtude') {
            return "Niveau d'étude";
        } else if (activeTab === 'typeFormation') {
            return "Type de formation";
        } else if (activeTab === 'domaineFormation') {
            return "Domaine de formation";
        } else if (activeTab === 'typeDiplome') {
            return "Type de diplôme";
        } else if (activeTab === 'listeLangue') {
            return "Langue";
        } else if (activeTab === 'competence') {
            return "Compétence";
        }
    };

    // Fonction pour obtenir l'icône appropriée
    const getTabIcon = (tab) => {
        if (tab === 'niveauEtude') {
            return <GraduationCap size={18} />;
        } else if (tab === 'typeFormation') {
            return <Briefcase size={18} />;
        } else if (tab === 'domaineFormation') {
            return <BookOpen size={18} />;
        } else if (tab === 'typeDiplome') {
            return <Award size={18} />;
        } else if (tab === 'listeLangue') {
            return <Globe size={18} />;
        } else if (tab === 'competence') {
            return <Code size={18} />;
        }
    };

    const renderTabs = () => {
        return (
            <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
                <button
                    className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'niveauEtude' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    onClick={() => setActiveTab('niveauEtude')}
                >
                    <GraduationCap size={18} className="mr-2" />
                    Niveaux d'étude
                </button>
                <button
                    className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'typeFormation' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    onClick={() => setActiveTab('typeFormation')}
                >
                    <Briefcase size={18} className="mr-2" />
                    Types de formation
                </button>
                <button
                    className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'domaineFormation' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    onClick={() => setActiveTab('domaineFormation')}
                >
                    <BookOpen size={18} className="mr-2" />
                    Domaines de formation
                </button>
                <button
                    className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'typeDiplome' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    onClick={() => setActiveTab('typeDiplome')}
                >
                    <Award size={18} className="mr-2" />
                    Types de diplôme
                </button>
                <button
                    className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'listeLangue' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    onClick={() => setActiveTab('listeLangue')}
                >
                    <Globe size={18} className="mr-2" />
                    Langues
                </button>
                <button
                    className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'competence' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    onClick={() => setActiveTab('competence')}
                >
                    <Code size={18} className="mr-2" />
                    Compétences
                </button>
            </div>
        );
    };

    const renderSearchAndPagination = () => {
        return (
            <div className="mb-6">
                {/* Barre de recherche simplifiée */}
                <div className="relative w-full sm:w-64 md:w-80">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search size={16} className="text-gray-400" />
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                        placeholder={`Rechercher un ${getEntityName().toLowerCase()}...`}
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </div>
            </div>
        );
    };

    const renderTable = () => {
        let data = getPaginatedData();
        let columns = [];
        
        if (activeTab === 'niveauEtude') {
            columns = ['Niveau', 'Date de création', 'Dernière mise à jour'];
        } else if (activeTab === 'typeFormation') {
            columns = ['Libellé', 'Date de création', 'Dernière mise à jour'];
        } else if (activeTab === 'domaineFormation') {
            columns = ['Nom', 'Date de création', 'Dernière mise à jour'];
        } else if (activeTab === 'typeDiplome') {
            columns = ['Nom', 'Date de création', 'Dernière mise à jour'];
        } else if (activeTab === 'listeLangue') {
            columns = ['Langue', 'Date de création', 'Dernière mise à jour'];
        } else if (activeTab === 'competence') {
            columns = ['Nom', 'Date de création', 'Dernière mise à jour'];
        }
        
        const filteredData = getFilteredData();
        if (filteredData.length === 0 && searchTerm) {
            return (
                <div className="bg-white rounded-xl shadow-md p-8 text-center">
                    <div className="mb-6">
                        <div className="h-20 w-20 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 mx-auto">
                            <Search size={36} strokeWidth={1.5} />
                        </div>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Aucun résultat trouvé</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                        Aucun {getEntityName().toLowerCase()} ne correspond à votre recherche "{searchTerm}".
                    </p>
                </div>
            );
        }

        if (filteredData.length === 0) {
            return (
                <div className="bg-white rounded-xl shadow-md p-8 text-center">
                    <div className="mb-6">
                        <div className="h-20 w-20 rounded-full bg-orange-100 flex items-center justify-center text-orange-500 mx-auto">
                            {getTabIcon(activeTab)}
                        </div>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Aucun élément</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                        Ajoutez des {getEntityName().toLowerCase()}s pour enrichir votre référentiel.
                    </p>
                </div>
            );
        }

        return (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {columns.map((col, index) => (
                                    <th key={index} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {col}
                                    </th>
                                ))}
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {data.map((item) => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                    {activeTab === 'niveauEtude' && (
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.niveau}</td>
                                    )}
                                    {activeTab === 'typeFormation' && (
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.libelle}</td>
                                    )}
                                    {activeTab === 'domaineFormation' && (
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.nom}</td>
                                    )}
                                    {activeTab === 'typeDiplome' && (
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.nom}</td>
                                    )}
                                    {activeTab === 'listeLangue' && (
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.langue}</td>
                                    )}
                                    {activeTab === 'competence' && (
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.nom}</td>
                                    )}
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        {formatDate(item.create_at)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        {formatDate(item.update_at)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex justify-end space-x-2">
                                            <button
                                                onClick={() => openEditModal(item)}
                                                className="bg-orange-50 hover:bg-orange-100 text-orange-600 p-2 rounded-full transition-colors duration-200"
                                                title="Modifier"
                                            >
                                                <Edit3 size={16} />
                                            </button>
                                            <button
                                                onClick={() => setDeleteConfirmation(item.id)}
                                                className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-full transition-colors duration-200"
                                                title="Supprimer"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination au pied du tableau */}
                {filteredData.length > 0 && (
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-center mb-3 sm:mb-0">
                                <label htmlFor="items-per-page" className="mr-2 text-sm text-gray-600">Afficher:</label>
                                <select
                                    id="items-per-page"
                                    className="border border-gray-300 rounded-md py-1 px-2 text-sm focus:ring-orange-500 focus:border-orange-500"
                                    value={itemsPerPage}
                                    onChange={handleItemsPerPageChange}
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                </select>
                                <span className="text-sm text-gray-600 ml-4">
                                    {`${(currentPage - 1) * itemsPerPage + 1} à ${Math.min(currentPage * itemsPerPage, filteredData.length)} sur ${filteredData.length} éléments`}
                                </span>
                            </div>

                            {totalPages > 1 && (
                                <div className="flex justify-center">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className={`inline-flex items-center px-2 py-1 border border-gray-300 rounded-l-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter(page =>
                                            page === 1 ||
                                            page === totalPages ||
                                            (page >= currentPage - 1 && page <= currentPage + 1)
                                        )
                                        .map((page, index, array) => {
                                            // Ajouter des points de suspension si nécessaire
                                            if (index > 0 && array[index - 1] !== page - 1) {
                                                return (
                                                    <React.Fragment key={`ellipsis-${page}`}>
                                                        <span className="inline-flex items-center px-3 py-1 border border-gray-300 bg-white text-gray-600">
                                                            ...
                                                        </span>
                                                        <button
                                                            onClick={() => handlePageChange(page)}
                                                            className={`inline-flex items-center px-3 py-1 border border-gray-300 ${currentPage === page ? 'bg-orange-50 text-orange-600 border-orange-500' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                                        >
                                                            {page}
                                                        </button>
                                                    </React.Fragment>
                                                );
                                            }
                                            return (
                                                <button
                                                    key={page}
                                                    onClick={() => handlePageChange(page)}
                                                    className={`inline-flex items-center px-3 py-1 border border-gray-300 ${currentPage === page ? 'bg-orange-50 text-orange-600 border-orange-500' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                                >
                                                    {page}
                                                </button>
                                            );
                                        })
                                    }
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className={`inline-flex items-center px-2 py-1 border border-gray-300 rounded-r-md ${currentPage === totalPages ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderContent = () => {
        if (loading && 
            ((activeTab === 'niveauEtude' && niveauxEtude.length === 0) || 
             (activeTab === 'typeFormation' && typesFormation.length === 0) || 
             (activeTab === 'domaineFormation' && domainesFormation.length === 0) ||
             (activeTab === 'typeDiplome' && typesDiplome.length === 0) ||
             (activeTab === 'listeLangue' && listeLangues.length === 0) ||
             (activeTab === 'competence' && competences.length === 0))) {
            return (
                <div className="flex justify-center items-center h-full">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500"></div>
                </div>
            );
        }

        return (
            <>
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-[#437011] mb-2">
                            <Settings className="inline-block mr-2" />
                            Paramètres
                        </h1>
                        <p className="text-gray-600">Gérez vos référentiels</p>
                    </div>
                    <button
                        onClick={openAddModal}
                        className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-orange-700 transition mt-4 md:mt-0 w-full md:w-auto justify-center"
                    >
                        <Plus size={18} className="mr-2" />
                        Ajouter un {getEntityName().toLowerCase()}
                    </button>
                </div>

                {renderTabs()}
                {renderSearchAndPagination()}
                {renderTable()}
            </>
        );
    };

    return (
        <div className="flex min-h-screen bg-[#F4F8E9]">
            <Sidebar
                isOpen={isSidebarOpen}
                menuItems={DemandeurMenu()}
                onClose={() => setIsSidebarOpen(false)}
            />

            <div className="flex-1 flex flex-col w-full">
                <Header
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                    user={user}
                />
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-20 lg:pb-8 w-full">
                    {renderContent()}
                </main>
            </div>

            {/* Modal pour ajouter/éditer */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-800">
                                {isEditMode ? `Modifier le ${getEntityName().toLowerCase()}` : `Ajouter un ${getEntityName().toLowerCase()}`}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6">
                            {activeTab === 'niveauEtude' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Niveau <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="niveau"
                                        value={formData.niveau}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.niveau ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                        placeholder="Ex: Bac+2 , Bac+3, Bac+4, Bac+5, etc."
                                    />
                                    {errors.niveau && <p className="mt-1 text-sm text-red-600">{errors.niveau}</p>}
                                </div>
                            )}

                            {activeTab === 'typeFormation' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Libellé <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="libelle"
                                        value={formData.libelle}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.libelle ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                        placeholder="Ex: Formation professionnelle, Formation continue, etc."
                                    />
                                    {errors.libelle && <p className="mt-1 text-sm text-red-600">{errors.libelle}</p>}
                                </div>
                            )}

                            {activeTab === 'domaineFormation' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Nom <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="nom"
                                        value={formData.nom}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.nom ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                        placeholder="Ex: Informatique, Gestion, Droit, etc."
                                    />
                                    {errors.nom && <p className="mt-1 text-sm text-red-600">{errors.nom}</p>}
                                </div>
                            )}

                            {activeTab === 'typeDiplome' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Nom <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="nom"
                                        value={formData.nom}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.nom ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                        placeholder="Ex: Licence, Master, Doctorat, etc."
                                    />
                                    {errors.nom && <p className="mt-1 text-sm text-red-600">{errors.nom}</p>}
                                </div>
                            )}

                            {activeTab === 'listeLangue' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Langue <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="langue"
                                        value={formData.langue}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.langue ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                        placeholder="Ex: Français, Anglais, Arabe, etc."
                                    />
                                    {errors.langue && <p className="mt-1 text-sm text-red-600">{errors.langue}</p>}
                                </div>
                            )}

                            {activeTab === 'competence' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Nom <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="nom"
                                        value={formData.nom}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.nom ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                        placeholder="Ex: Développement web, Design, Marketing, etc."
                                    />
                                    {errors.nom && <p className="mt-1 text-sm text-red-600">{errors.nom}</p>}
                                </div>
                            )}

                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`px-4 py-2 rounded-lg text-white ${loading ? 'bg-orange-400' : 'bg-orange-600 hover:bg-orange-700'}`}
                                >
                                    {loading ? (
                                        <span className="flex items-center">
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Enregistrement...
                                        </span>
                                    ) : (
                                        isEditMode ? "Mettre à jour" : "Ajouter"
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de confirmation de suppression */}
            {deleteConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Confirmer la suppression</h3>
                            <p className="text-gray-600 mb-6">
                                Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est irréversible.
                            </p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setDeleteConfirmation(null)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={() => handleDelete(deleteConfirmation)}
                                    className="px-4 py-2 rounded-lg text-white bg-red-600 hover:bg-red-700"
                                >
                                    Supprimer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ParametrePage;